import React, { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Eye, Award, Users, Target } from "lucide-react";

/** Logo helper */
function Logo({ src, alt, className }) {
  const fallback = "https://via.placeholder.com/150?text=Logo";
  const [current, setCurrent] = useState(src);

  useEffect(() => {
    setCurrent(src);
  }, [src]);

  return (
    <img
      src={current}
      alt={alt}
      className={className}
      loading="lazy"
      onError={() => current !== fallback && setCurrent(fallback)}
    />
  );
}

function AboutUs() {
  const companies = [
    { name: "Datoms", role: "Partner", logo: "/logos/datoms.webp" },
    { name: "Carbonout", role: "Partner", logo: "/logos/carbonout.jpeg" },
    { name: "Bon V Aero", role: "Partner", logo: "/logos/bonvaero.avif" },
    { name: "Swayogya Rehab", role: "Partner", logo: "/logos/swayogya.png" },
    { name: "CIFA (Bhubaneswar)", role: "Govt. Body", logo: "/logos/cifa.jpeg" },
  ];

  const marqueeCompanies = useMemo(
    () => [...companies, ...companies],
    [companies]
  );

  /** ✅ NEW DEMO SLIDE DATA */
  const demo = [
    { name: "Demo 1", role: "Demo Client", logo: "/logos/demo1.png" },
    { name: "Demo 2", role: "Demo Partner", logo: "/logos/demo2.png" },
    { name: "Demo 3", role: "Demo Client", logo: "/logos/demo3.png" },
  ];

  const marqueeDemo = useMemo(() => [...demo, ...demo], [demo]);

  return (
    <section
      id="about"
      className="relative bg-gradient-to-b from-[#0A2A43] via-[#1E90FF] to-[#87CEEB] text-white py-20 overflow-hidden"
    >
      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/circuits.png')] opacity-5"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        
        {/* Heading */}
        <motion.h2
          className="text-4xl md:text-5xl font-extrabold text-center mb-6"
          initial={{ opacity: 0, y: -40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          About <span className="text-blue-200">Us</span>
        </motion.h2>

        <motion.p
          className="text-center text-blue-100 max-w-3xl mx-auto mb-16 text-lg"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <span className="text-white font-semibold">SPK MAK TECHNOLOGIES</span> is
          an innovation-driven company focused on Artificial Intelligence,
          Robotics, and Automation.
        </motion.p>

        {/* Vision & Mission */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-16">
          <motion.div
            className="bg-blue-600/80 rounded-2xl p-10 shadow-lg text-center border border-blue-300"
            initial={{ opacity: 0, x: -80 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Eye className="w-12 h-12 mx-auto mb-4 text-blue-200" />
            <h3 className="text-3xl font-bold mb-3">Our Vision</h3>
            <p className="text-blue-100">
              To contribute to the growth of Digital world through the development
              of superior green technologies & products.
            </p>
          </motion.div>

          <motion.div
            className="bg-blue-600/80 rounded-2xl p-10 shadow-lg text-center border border-blue-300"
            initial={{ opacity: 0, x: 80 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Target className="w-12 h-12 mx-auto mb-4 text-blue-200" />
            <h3 className="text-3xl font-bold mb-3">Our Mission</h3>
            <p className="text-blue-100">
              A Global Leader in electronics design and Manufacturer.
            </p>
          </motion.div>
        </div>

        {/* Achievements */}
        <motion.div
          className="bg-blue-600/80 rounded-2xl p-10 shadow-lg mb-16 text-center border border-blue-300"
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Award className="w-12 h-12 mx-auto mb-4 text-blue-200" />
          <h3 className="text-3xl font-bold mb-3">Our Achievements</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-6">
            <div className="bg-blue-500 rounded-xl py-6">
              <h4 className="text-2xl font-bold">50+</h4>
              <p className="text-blue-100">Projects</p>
            </div>
            <div className="bg-blue-500 rounded-xl py-6">
              <h4 className="text-2xl font-bold">25+</h4>
              <p className="text-blue-100">Clients</p>
            </div>
            <div className="bg-blue-500 rounded-xl py-6">
              <h4 className="text-2xl font-bold">10+</h4>
              <p className="text-blue-100">Awards</p>
            </div>
          </div>
        </motion.div>

        {/* OUR INDUSTRIAL PARTNERS & CLIENTS */}
        <motion.div
          className="bg-blue-600/80 rounded-2xl p-10 shadow-lg text-center border border-blue-300"
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Users className="w-12 h-12 mx-auto mb-4 text-blue-200" />
          <h3 className="text-3xl font-bold mb-3">Our Industrial Partners & Clients</h3>

          <div className="relative overflow-x-hidden">
            <div
              className="marquee flex items-center gap-8 py-4"
              style={{ animation: "marquee-left 8s linear infinite" }}
            >
              {marqueeCompanies.map((company, index) => (
                <div
                  key={index}
                  className="min-w-[200px] bg-blue-500 rounded-xl p-6 flex flex-col items-center gap-3"
                >
                  <div className="w-28 h-28 bg-white rounded-md flex items-center justify-center p-2">
                    <Logo src={company.logo} alt={company.name} className="max-w-full max-h-full object-contain" />
                  </div>
                  <h4 className="text-lg font-semibold">{company.name}</h4>
                  <p className="text-blue-100 text-sm">{company.role}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* ✅ NEW DEMO SLIDE (ADDED) */}
       <motion.div
  className="bg-blue-600/80 rounded-2xl p-10 shadow-lg text-center border border-blue-300 mt-16"
  initial={{ opacity: 0, y: 60 }}
  whileInView={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.8 }}
>
  <Users className="w-12 h-12 mx-auto mb-4 text-blue-200" />
  <h3 className="text-3xl font-bold mb-3">Industry-Academia Collaboration</h3>

  <div className="relative overflow-x-hidden">
    <div
      className="marquee flex items-center gap-8 py-4"
      style={{ animation: "marquee-left 8s linear infinite" }}
    >

      {/* Birla Global University */}
      <div className="min-w-[200px] bg-blue-500 rounded-xl p-6 flex flex-col items-center gap-3">
        <div className="w-28 h-28 bg-white rounded-md flex items-center justify-center p-2">
          <Logo
            src="/logos/birla.png"
            alt="Birla Global University"
            className="max-w-full max-h-full object-contain"
          />
        </div>
        <h4 className="text-lg font-semibold">Birla Global University</h4>
        <p className="text-blue-100 text-sm">Academia Partner</p>
      </div>

      {/* SOA ITER */}
      <div className="min-w-[200px] bg-blue-500 rounded-xl p-6 flex flex-col items-center gap-3">
        <div className="w-28 h-28 bg-white rounded-md flex items-center justify-center p-2">
          <Logo
            src="/logos/soa-iter.jpeg"
            alt="SOA ITER Bhubaneswar"
            className="max-w-full max-h-full object-contain"
          />
        </div>
        <h4 className="text-lg font-semibold">SOA ITER Bhubaneswar</h4>
        <p className="text-blue-100 text-sm">Academic Institute</p>
      </div>

      {/* MIT-WPU */}
      <div className="min-w-[200px] bg-blue-500 rounded-xl p-6 flex flex-col items-center gap-3">
        <div className="w-28 h-28 bg-white rounded-md flex items-center justify-center p-2">
          <Logo
            src="/logos/mit-wpu.jpeg"
            alt="MIT-WPU Pune"
            className="max-w-full max-h-full object-contain"
          />
        </div>
        <h4 className="text-lg font-semibold">MIT-WPU Pune</h4>
        <p className="text-blue-100 text-sm">University Partner</p>
      </div>

      {/* Duplicate for smooth marquee */}
      <div className="min-w-[200px] bg-blue-500 rounded-xl p-6 flex flex-col items-center gap-3">
        <div className="w-28 h-28 bg-white rounded-md flex items-center justify-center p-2">
          <Logo src="/logos/birla.png" alt="Birla Global University" className="max-w-full max-h-full object-contain" />
        </div>
        <h4 className="text-lg font-semibold">Birla Global University</h4>
        <p className="text-blue-100 text-sm">Academia Partner</p>
      </div>

      <div className="min-w-[200px] bg-blue-500 rounded-xl p-6 flex flex-col items-center gap-3">
        <div className="w-28 h-28 bg-white rounded-md flex items-center justify-center p-2">
          <Logo src="/logos/soa-iter.jpeg" alt="SOA ITER Bhubaneswar" className="max-w-full max-h-full object-contain" />
        </div>
        <h4 className="text-lg font-semibold">SOA ITER Bhubaneswar</h4>
        <p className="text-blue-100 text-sm">Academic Institute</p>
      </div>

      <div className="min-w-[200px] bg-blue-500 rounded-xl p-6 flex flex-col items-center gap-3">
        <div className="w-28 h-28 bg-white rounded-md flex items-center justify-center p-2">
          <Logo src="/logos/mit-wpu.jpeg" alt="MIT-WPU Pune" className="max-w-full max-h-full object-contain" />
        </div>
        <h4 className="text-lg font-semibold">MIT-WPU Pune</h4>
        <p className="text-blue-100 text-sm">University Partner</p>
      </div>

    </div>
  </div>
</motion.div>


      </div>

      <style>{`
        @keyframes marquee-left {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </section>
  );
}

export default AboutUs;
